import { offset, shift } from '@floating-ui/dom'
import { Controller } from '@hotwired/stimulus'
import { FetchRequest } from '@rails/request.js'
import Shepherd from 'shepherd.js'

// This controller requires step targets to be present in the DOM.
// Each step element should have a unique tutorial name, index, title, and text dataset attributes.
// You can also add `data-show-nav="true"` to open the navigation panel when displaying the step.
//
// **The tutorial name has to be "allowed" in the `tutorial.rb` model via the `:name` validator.**
//
// Example steps:
// ```html
// <div data-controller="tutorial">
//   <div
//      data-tutorial-target="step"
//      data-tutorial-name="tutorial1"
//      data-index="1"
//      data-title="Step 2"
//      data-text="This is the second step."
//      data-show-nav="true">
//   </div>
//   <div
//      data-tutorial-target="step"
//      data-tutorial-name="tutorial1"
//      data-index="0"
//      data-title="Step 1"
//      data-text="This is the first step."
//      data-show-nav="true">
//   </div>
// </div>
// ```
//
// Example start button:
// ```html
// <div data-controller="tutorial">
//   <div
//      data-tutorial-target="start"
//      data-tutorial-name="tutorial1"
//      data-navigate-to="/path/to/tutorial1">
//      Start Tutorial
//   </div>
// </div>
// ```

export default class extends Controller {
  static targets = ['step', 'start']
  static outlets = ['nav']

  async connect() {
    this.startTargets.forEach((startTarget) => {
      startTarget.addEventListener('click', () => {
        this.startTutorial(startTarget, startTarget.dataset)
      })
    })

    const steps = this.stepTargets.map((step) => ({
      tutorialName: step.dataset.tutorialName,
      title: step.dataset.title,
      text: step.dataset.text,
      index: step.dataset.index,
      attachTo: step.offsetParent == null ? null : { element: step, on: 'auto' },
      showNavigation: step.dataset.showNav === 'true',
      scrollTo: step.dataset.scrollTo !== 'false',
      reloadOnCompletion: step.dataset.reloadOnCompletion === 'true',
      extraHighlights: step.dataset.extraHighlights?.split(' ')?.map((highlight) => highlight.trim()),
    }))

    const groupedSteps = steps.reduce((acc, step) => {
      const key = step.tutorialName

      if (!acc[key]) {
        acc[key] = []
      }

      acc[key].push(step)

      return acc
    }, {})

    const tutorials = Object.entries(groupedSteps).map(async ([tutorialName, steps]) => {
      const response = await fetch(`/tutorials/${tutorialName}/status`)
      const state = await response.json()

      const reloadOnCompletion = steps.some((step) => step.reloadOnCompletion)

      return {
        name: tutorialName,

        reloadOnCompletion,
        state,
        steps,
      }
    })

    this.activeTutorials = (await Promise.all(tutorials))
      .filter((tutorial) => !tutorial.state.completed)
      .map(({ name, steps, reloadOnCompletion }) => {
        const tutorial = new Shepherd.Tour({
          tourName: name,
          useModalOverlay: true,
          defaultStepOptions: {
            floatingUIOptions: {
              middleware: [offset({ mainAxis: 24, crossAxis: 0 }), shift({ padding: 4 })],
            },
            modalOverlayOpeningRadius: 16,
            modalOverlayOpeningPadding: 8,
          },
        })

        tutorial.reloadOnCompletion = reloadOnCompletion

        const stepCount = steps.length

        steps
          .sort((a, b) => a.index - b.index)
          .forEach((step, index) => {
            const buttons = [
              {
                text: index === stepCount - 1 ? gon.messages.generic.finish : gon.messages.generic.next,
                action: tutorial.next,
                classes: 'is-button is-small is-primary',
              },
            ]

            if (index > 0) {
              buttons.unshift({
                text: gon.messages.generic.previous,
                classes: 'is-button is-small is-ghost normal tw:text-primary-400 tw:hover:text-primary-500 tw:hover:bg-primary-100',
                action: tutorial.back,
              })
            }

            if (index !== stepCount - 1) {
              buttons.unshift({
                text: gon.messages.generic.skip,
                classes: 'is-button is-small is-ghost normal tw:text-primary-300 tw:hover:text-primary-500 tw:hover:bg-primary-100 tw:-order-2',
                action: tutorial.complete,
              })
            }

            tutorial.addStep({
              id: `${step.tutorialName}-step-${index}`,
              title: step.title,
              text: step.text,
              attachTo: step.attachTo,
              scrollTo: step.scrollTo ? { behavior: 'smooth', block: 'nearest' } : false,
              extraHighlights: step.extraHighlights,

              buttons,

              when: {
                show: () => {
                  if (step.showNavigation && this.hasNavOutlet) {
                    this.navOutlet.open()
                  }
                },
                hide: () => {
                  if (step.showNavigation && this.hasNavOutlet) {
                    this.navOutlet.close()
                  }
                },
              },
            })
          })

        return tutorial
      })
      .map((tutorial, index, tutorials) => {
        tutorial.nextTutorial = tutorials[index + 1]

        for (const event of ['complete', 'cancel']) {
          tutorial.once(event, () => this.completeTutorial(tutorial))
        }

        return tutorial
      })

    this.activeTutorials[0]?.start()
  }

  async completeTutorial(tutorial) {
    const request = new FetchRequest('PATCH', `/tutorials/${tutorial.options.tourName}/complete`)

    const task = request.perform()

    if (tutorial.reloadOnCompletion) {
      await task
      window.location.reload()

      return
    }

    if (this.hasNavOutlet) {
      this.navOutlet.close()
    }

    tutorial.nextTutorial?.start()
  }

  async startTutorial(targetElem, options = {}) {
    const tutorialName = options.tutorialName
    const navigateTo = options.navigateTo
    const resetRequest = new FetchRequest('PATCH', `/tutorials/${tutorialName}/reset`)

    const task = resetRequest.perform()

    if (options.disableButton != 'false') {
      this.setLoader(targetElem)
    }

    await task

    if (navigateTo) {
      window.location.href = navigateTo
    }
  }

  setLoader(element) {
    if (!element) {
      return
    }

    element.classList.add('is-disabled')

    element.innerHTML = `
      <div class="tw:flex tw:items-center tw:justify-center tw:w-full tw:h-full">
        <div class="tw:animate-spin tw:rounded-full tw:h-8 tw:w-8 tw:border-4 tw:border-white tw:border-t-transparent">
        </div>
      </div>
    `
  }
}
